// Mantine CSS is auto-imported by vite-plugin-mantine-css.
// Just use `import { Button } from '@mantine/core'` — the plugin handles CSS.
import 'virtual:mantine-css';
import './global.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { App } from './App';
import { theme } from './theme';
import { CookieConsent } from './components/shared/CookieConsent';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: (failureCount, error) => {
        // Don't retry client errors (4xx) except 408/429
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        // Retry server errors (5xx) and network failures up to 3 times
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, 15000), // 1s, 2s, 4s ... max 15s
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={theme} defaultColorScheme="dark">
          <Notifications position="bottom-right" autoClose={4000} />
          <BrowserRouter>
            <App />
            <CookieConsent />
          </BrowserRouter>
        </MantineProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
);
